import React from "react";
import dayjs from "dayjs";
import {
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type LineConfig = {
  key: string;
  color: string;
  name: string;
};

type Props<T extends Record<string, unknown>> = {
  data: T[];
  xKey: keyof T & string;
  lines: LineConfig[];
  onRangeSelect: (start: number, end: number) => void;
};

export const ZoomableMultiLineChart = <T extends Record<string, unknown>>({
  data,
  xKey,
  lines,
  onRangeSelect,
}: Props<T>) => {
  const [refAreaLeft, setRefAreaLeft] = React.useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = React.useState<number | null>(null);

  const reset = () => {
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const handleZoom = () => {
    if (refAreaLeft === null || refAreaRight === null || refAreaLeft === refAreaRight) {
      reset();
      return;
    }
    onRangeSelect(Math.min(refAreaLeft, refAreaRight), Math.max(refAreaLeft, refAreaRight));
    reset();
  };

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <LineChart
          data={data}
          onMouseDown={(event) => {
            const raw = event?.activeLabel;
            const value = typeof raw === "number" ? raw : Number(raw);
            setRefAreaLeft(Number.isNaN(value) ? null : value);
            setRefAreaRight(null);
          }}
          onMouseMove={(event) => {
            if (refAreaLeft === null) {
              return;
            }
            const raw = event?.activeLabel;
            const value = typeof raw === "number" ? raw : Number(raw);
            if (!Number.isNaN(value)) {
              setRefAreaRight(value);
            }
          }}
          onMouseUp={handleZoom}
        >
          <XAxis
            dataKey={xKey}
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(value) => dayjs(value).format("YYYY-MM-DD")}
          />
          <YAxis />
          <Tooltip labelFormatter={(value) => dayjs(value as number).format("YYYY-MM-DD")} />
          {lines.map((line) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              stroke={line.color}
              dot={false}
              name={line.name}
            />
          ))}
          {refAreaLeft !== null && refAreaRight !== null && (
            <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
